'use client';
import {
  Search,
  FileWarning,
  Wallet,
  MessageSquareWarning,
} from 'lucide-react';
import { useTranslations } from 'next-intl';

export function PainPoints() {
  const t = useTranslations('landing.painPoints');

  const painPoints = [
    {
      icon: Search,
      title: t('pain1Title'),
      description: t('pain1Desc'),
      stat: '15+',
      statSuffix: ' שע׳',
      statLabel: t('pain1StatLabel'),
      accent: 'seafoam',
    },
    {
      icon: FileWarning,
      title: t('pain2Title'),
      description: t('pain2Desc'),
      stat: '40%',
      statSuffix: '',
      statLabel: t('pain2StatLabel'),
      accent: 'terracotta',
    },
    {
      icon: Wallet,
      title: t('pain3Title'),
      description: t('pain3Desc'),
      stat: '23%',
      statSuffix: '',
      statLabel: t('pain3StatLabel'),
      accent: 'seafoam',
    },
    {
      icon: MessageSquareWarning,
      title: t('pain4Title'),
      description: t('pain4Desc'),
      stat: '8+',
      statSuffix: '',
      statLabel: t('pain4StatLabel'),
      accent: 'terracotta',
    },
  ];

  return (
    <section
      className="relative py-20 md:py-28 overflow-hidden"
      style={{ background: 'hsl(210 15% 18%)' }}
    >
      {/* Background dot pattern */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #88B7AD 1px, transparent 0)`,
          backgroundSize: '36px 36px',
        }}
      />

      {/* Gradient blobs — brand colors */}
      <div
        className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full blur-3xl opacity-10"
        style={{ background: '#88B7AD' }}
      />
      <div
        className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full blur-3xl opacity-10"
        style={{ background: '#BC6043' }}
      />

      <div className="container-landing relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <span
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-6 border"
            style={{
              background: 'rgba(136,183,173,0.12)',
              borderColor: 'rgba(136,183,173,0.3)',
              color: '#88B7AD',
            }}
          >
            {t('sectionBadge')}
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display tracking-tight text-balance text-white">
            {t('heading')}{' '}
            <span style={{ color: '#BC6043' }}>{t('headingHighlight')}</span>
          </h2>
          <p
            className="mt-4 md:mt-6 text-lg md:text-xl text-balance"
            style={{ color: 'rgba(255,255,255,0.6)' }}
          >
            {t('subheading')}
          </p>
        </div>

        {/* Pain points grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {painPoints.map((point, index) => {
            const isSeafoam = point.accent === 'seafoam';
            const accentColor = isSeafoam ? '#88B7AD' : '#BC6043';
            const accentBg = isSeafoam
              ? 'rgba(136,183,173,0.12)'
              : 'rgba(188,96,67,0.12)';
            const accentBorder = isSeafoam
              ? 'rgba(136,183,173,0.2)'
              : 'rgba(188,96,67,0.2)';

            return (
              <div
                key={point.title}
                className="group relative"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Card */}
                <div
                  className="relative h-full p-6 md:p-8 rounded-2xl backdrop-blur-sm transition-all duration-300 hover:scale-[1.02]"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${accentBorder}`,
                  }}
                >
                  {/* Icon */}
                  <div
                    className="w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center mb-5 transition-all duration-300 group-hover:scale-110"
                    style={{ background: accentBg }}
                  >
                    <point.icon
                      className="w-6 h-6 md:w-7 md:h-7"
                      style={{ color: accentColor }}
                    />
                  </div>

                  {/* Content */}
                  <h3 className="text-lg md:text-xl font-semibold mb-3 text-white">
                    {point.title}
                  </h3>
                  <p
                    className="text-sm md:text-base leading-relaxed mb-6"
                    style={{ color: 'rgba(255,255,255,0.55)' }}
                  >
                    {point.description}
                  </p>

                  {/* Stat */}
                  <div
                    className="pt-4"
                    style={{ borderTop: `1px solid ${accentBorder}` }}
                  >
                    <div
                      className="text-2xl md:text-3xl font-display font-bold"
                      style={{ color: accentColor }}
                    >
                      {point.stat}
                      {point.statSuffix}
                    </div>
                    <div
                      className="text-sm mt-1"
                      style={{ color: 'rgba(255,255,255,0.4)' }}
                    >
                      {point.statLabel}
                    </div>
                  </div>
                </div>

                {/* Hover glow */}
                <div
                  className="absolute inset-0 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity -z-10"
                  style={{ background: accentBg }}
                />
              </div>
            );
          })}
        </div>

        {/* Bottom statement */}
        <div className="mt-16 md:mt-20 text-center">
          <p
            className="text-xl md:text-2xl font-display"
            style={{ color: 'rgba(255,255,255,0.75)' }}
          >
            {t('bottomStatement')}{' '}
            <span style={{ color: '#88B7AD' }}>{t('bottomHighlight')}</span>
          </p>
        </div>
      </div>
    </section>
  );
}
